import {noop} from '@benchling/js-utils';
import React, {useState} from 'react';
import isEqual from 'react-fast-compare';

import {Owner} from 'auth/PermissionConstants';
import * as Context from 'Context';
import {ActionOrigin, StudyType} from 'graphql-client/__generated/graphqlGlobalTypes';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {NameTemplatePart} from 'name-templates/NameTemplateConstants';
import {graphQLNameTemplatePartsToProps} from 'name-templates/NameTemplateUtil';
import notifications from 'notifications';
import {ClientSchemaField} from 'registry/field/FieldTable';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {massageFieldsForSubmit} from 'schemas/SchemaUtil';
import {
  setStudyNameTemplateParts,
  StudySchemaSpec,
  updateStudySchema,
} from 'studies/study-schemas/StudySchemaDAO';
import {withSpecErrorHandling} from 'studies/study-schemas/StudySchemaDAO';
import StudySchemaMetadataSettings from 'studies/study-schemas/StudySchemaMetadataSettings';
import {Tab, TabContainer} from 'ui-library/TabContainer';
import {withLoadingScreen} from 'util/Promises';

import {
  StudySchemaQuery,
  StudySchemaQuery_studySchema_StudySchemaNode_entryTemplates as EntryTemplateIconOrInaccessibleInfo,
} from './__generated/StudySchemaQuery';
import {massageEntryTemplatesForSubmit} from './StudySchemaCreateFlow';
import StudySchemaPermissionSettings from './StudySchemaPermissionSettings';

type StudySchema = ExtractByTypename<StudySchemaQuery['studySchema'], 'StudySchemaNode'>;

enum Tabs {
  STUDY_SCHEMA_EDITOR = 'STUDY_SCHEMA_EDITOR',
  STUDY_SCHEMA_ACCESS_POLICIES = 'STUDY_SCHEMA_ACCESS_POLICIES',
}

export function getSpecForRequest(
  fields: Array<ClientSchemaField>,
  idPrefix: string,
  isDesignApprovalRequired: boolean,
  entryTemplates: Array<EntryTemplateIconOrInaccessibleInfo>,
  studyType: StudyType
): StudySchemaSpec {
  return {
    fields: massageFieldsForSubmit(fields),
    idPrefix,
    isDesignApprovalRequired,
    template_api_ids: massageEntryTemplatesForSubmit(entryTemplates),
    studyType,
  };
}

export interface ExistingStudySchemaEditorProps {
  schema: StudySchema;
  ownerChoices: Array<Owner>;
  refetch: () => void;
}
export default function ExistingStudySchemaEditor(props: ExistingStudySchemaEditorProps): JSX.Element {
  const [activeTab, setActiveTab] = useState<Tabs>(Tabs.STUDY_SCHEMA_EDITOR);
  const updateSchema = ({name, spec}: {name?: string; spec?: StudySchemaSpec}): void => {
    withLoadingScreen(
      (async () => {
        await Context.usingAsync(
          {analyticsMetadata: {actionOrigin: ActionOrigin.SETTINGS_STUDY_SCHEMA_PAGE}},
          null,
          async () => {
            const response = await withSpecErrorHandling(
              updateStudySchema({
                schemaId: props.schema.id,
                name: name || props.schema.name,
                spec:
                  spec ||
                  getSpecForRequest(
                    props.schema.allFields,
                    props.schema.prefix,
                    props.schema.isDesignApprovalRequired,
                    props.schema.entryTemplates,
                    props.schema.studyType
                  ),
              })
            );
            if (response == null) {
              // Refetch to avoid retaining changes that failed to save in the UI
              props.refetch();
              return;
            }
            notifications.success('Updated study schema successfully');
            props.refetch();
          }
        );
      })(),
      'Updating study schema...'
    );
  };

  const setNameTemplate = (parts: Array<NameTemplatePart>): Promise<boolean> =>
    withLoadingScreen(
      (async () => {
        const result = await Context.usingAsync(
          {analyticsMetadata: {actionOrigin: ActionOrigin.SETTINGS_STUDY_SCHEMA_PAGE}},
          null,
          async () => {
            const response = await withSpecErrorHandling(setStudyNameTemplateParts(props.schema.id, parts));
            if (response == null) {
              // Refetch to avoid retaining changes that failed to save in the UI
              props.refetch();
              return false;
            }
            notifications.success('Updated name template successfully');
            props.refetch();
            return true;
          }
        );
        return result;
      })(),
      'Updating name template...'
    );

  const setName = (name: string): void => {
    updateSchema({name});
  };

  const setPrefix = (prefix: string): void => {
    updateSchema({
      spec: getSpecForRequest(
        props.schema.allFields,
        prefix,
        props.schema.isDesignApprovalRequired,
        props.schema.entryTemplates,
        props.schema.studyType
      ),
    });
  };

  const setEntryTemplates = (entryTemplates: Array<EntryTemplateIconOrInaccessibleInfo>): void => {
    updateSchema({
      spec: getSpecForRequest(
        props.schema.allFields,
        props.schema.prefix,
        props.schema.isDesignApprovalRequired,
        entryTemplates,
        props.schema.studyType
      ),
    });
  };

  const handleFieldsChange = (newFields: Array<ClientSchemaField>): void => {
    const fieldsWithoutCid = newFields.map((field) => {
      const {cid, ...fieldWithoutCid} = field;
      return fieldWithoutCid;
    }) as Array<SchemaField>; // TODO(BNCH-30720) Fix SchemaField/ClientSchemaField type issues

    if (isEqual(fieldsWithoutCid, props.schema.allFields)) {
      return;
    }
    updateSchema({
      spec: getSpecForRequest(
        newFields,
        props.schema.prefix,
        props.schema.isDesignApprovalRequired,
        props.schema.entryTemplates,
        props.schema.studyType
      ),
    });
  };

  const setIsDesignApprovalRequired = (isDesignApprovalRequired: boolean): void => {
    updateSchema({
      spec: getSpecForRequest(
        props.schema.allFields,
        props.schema.prefix,
        isDesignApprovalRequired,
        props.schema.entryTemplates,
        props.schema.studyType
      ),
    });
  };

  return (
    <TabContainer
      onRequestTabChange={(tab: Tabs) => {
        setActiveTab(tab);
      }}
      activeTab={activeTab}
      className="u-flex-grow"
    >
      <Tab tabKey={Tabs.STUDY_SCHEMA_EDITOR} title="Study Schema Settings">
        <StudySchemaMetadataSettings
          ownerId={props.schema.organization.id}
          setOwnerId={noop /* Owner can only be set on creation */}
          name={props.schema.name}
          nameTemplateParts={graphQLNameTemplatePartsToProps(
            props.schema.nameTemplates.length > 0 ? props.schema.nameTemplates[0].nameTemplateParts : []
          )}
          setNameTemplateParts={setNameTemplate}
          setName={setName}
          prefix={props.schema.prefix}
          setPrefix={setPrefix}
          fields={props.schema.allFields}
          setFields={handleFieldsChange}
          isDesignApprovalRequired={props.schema.isDesignApprovalRequired}
          setIsDesignApprovalRequired={setIsDesignApprovalRequired}
          ownerChoices={props.ownerChoices}
          isBeingCreated={false}
          schemaId={props.schema.id}
          schema={props.schema}
          entryTemplates={props.schema.entryTemplates}
          setEntryTemplates={setEntryTemplates}
          studyType={props.schema.studyType}
          setStudyType={() => {
            throw new Error('User unexpectedly tried to set study type after creation');
          }}
          autoGenerateId={props.schema.prefix !== ''}
          setAutoGenerateId={() => {
            throw new Error('User unexpectedly tried to set auto-generate ID after creation');
          }}
        />
      </Tab>
      <Tab tabKey={Tabs.STUDY_SCHEMA_ACCESS_POLICIES} title="Access Policies">
        <StudySchemaPermissionSettings studySchemaId={props.schema.id} />
      </Tab>
    </TabContainer>
  );
}
